// Scan primitives for the scalar-value slot recognizer
// (`editorContextSlot.value.ts`) — value-slot plan T5a. Pure text scan
// (G11-rule: no tree-sitter); split out to keep the recognizer ≤200 lines.

import {
  skipBacktickId,
  skipBlockComment,
  skipBracketedId,
  skipDoubleQuoted,
  skipToEol,
} from './scanPrimitives';

// Lexical state of the cursor offset. A backward scan alone cannot tell the
// interior of a CLOSED string (`SELECT 'x = ▮'`) from an in-progress value
// operand (`= 'op▮`) — quote parity is ambiguous — so the recognizer walks
// forward from the statement start first. 'opaque' = comments and
// quoted-identifier interiors: never a value operand.
export type ValueTextState =
  | { readonly kind: 'code' }
  | { readonly kind: 'sq-string'; readonly openQuote: number }
  | { readonly kind: 'opaque' };

// Bracket/backtick engine gates mirror `editorContextScan.isInsideStringLiteral`
// (SqlEngine vocabulary). Dollar quoting is NOT handled: in production,
// in-string cursors never reach the slot seam (deriveContext/projection bail
// first); a dollar-quoted body containing `'` is an accepted parity gap.
export function scanValueTextState(
  source: string,
  from: number,
  offset: number,
  engine: string,
): ValueTextState {
  let i = from;
  while (i < offset) {
    const c = source.charCodeAt(i);
    if (c === 0x2d && source.charCodeAt(i + 1) === 0x2d) {
      const end = skipToEol(source, i + 2);
      if (offset < end || end >= source.length) return { kind: 'opaque' };
      i = end;
      continue;
    }
    if (c === 0x2f && source.charCodeAt(i + 1) === 0x2a) {
      const end = skipBlockComment(source, i + 2);
      if (offset < end || (offset === end && end === source.length)) return { kind: 'opaque' };
      i = end;
      continue;
    }
    if (c === 0x27) {
      const lit = scanSingleQuoted(source, i + 1);
      // In-progress operand: cursor inside the literal, or at the EOF of an
      // UNTERMINATED one (`= 'op▮`). A literal CLOSED at EOF (`= 'done'▮`)
      // is a complete RHS — the cursor stays in code state (C1 sol P2;
      // `skipSingleQuoted` alone can't tell the two apart, both return EOF).
      if (offset < lit.end || (offset === lit.end && !lit.closed)) {
        return { kind: 'sq-string', openQuote: i };
      }
      i = lit.end;
      continue;
    }
    const identEnd =
      c === 0x22 ? skipDoubleQuoted(source, i + 1)
      : (engine === 'mssql' || engine === 'sqlite') && c === 0x5b ? skipBracketedId(source, i + 1)
      : engine === 'mysql' && c === 0x60 ? skipBacktickId(source, i + 1)
      : -1;
    if (identEnd !== -1) {
      if (offset < identEnd) return { kind: 'opaque' };
      i = identEnd;
      continue;
    }
    i += 1;
  }
  return { kind: 'code' };
}

/** Like `skipSingleQuoted`, but reports whether a REAL closing quote was
 *  consumed — a trailing `''` escape pair leaves the literal open even when
 *  the source ends in a quote char. */
function scanSingleQuoted(
  source: string,
  from: number,
): { readonly end: number; readonly closed: boolean } {
  let i = from;
  while (i < source.length) {
    if (source.charCodeAt(i) === 0x27) {
      if (source.charCodeAt(i + 1) === 0x27) {
        i += 2;
        continue;
      }
      return { end: i + 1, closed: true };
    }
    i += 1;
  }
  return { end: source.length, closed: false };
}

export type ComparisonOperatorMatch = {
  /** Normalized upper: '=', '>=', 'LIKE', … */
  readonly operator: string;
  /** True for NOT LIKE / NOT ILIKE. */
  readonly negated: boolean;
  /** Offset of the operator's first char (of `NOT` when negated) — the LHS
   *  operand ends before this. */
  readonly opStart: number;
};

const TWO_CHAR_OPS: readonly string[] = ['<=', '>=', '<>', '!='];

/** The word-spelled infix comparison operators every dialect shares. Both take
 *  a single scalar operand on the right, which is the value slot this module
 *  recognizes. */
const WORD_OPS: ReadonlySet<string> = new Set(['LIKE', 'ILIKE']);

/**
 * The pattern-match operators a dialect adds on top of LIKE. Engine-scoped
 * ON PURPOSE, unlike the rest of this syntax-only module: the same word is an
 * infix operator in one dialect and the head of a completely different
 * construct in another, so a shared list would misread real SQL.
 *   - sqlite — `x GLOB 'p'`, `x REGEXP 'p'`, and `t MATCH 'q'` (FTS5) are all
 *     infix comparisons (script.20 §3 / script.34 §6).
 *   - mysql — `x REGEXP 'p'` / `x RLIKE 'p'` are infix, but `MATCH (cols)
 *     AGAINST ('q')` LEADS its predicate, so MATCH must not be listed.
 *   - oracle — `GRAPH_TABLE(g MATCH (…))` is a SQL/PGQ pattern head.
 *   - pgsql — MATCH belongs to FK clauses (`REFERENCES t(c) MATCH FULL`); its
 *     regex operators are symbolic (`~`, `~*`), not word-spelled.
 */
const DIALECT_WORD_OPS: ReadonlyMap<string, ReadonlySet<string>> = new Map([
  ['sqlite', new Set(['GLOB', 'MATCH', 'REGEXP'])],
  ['mysql', new Set(['REGEXP', 'RLIKE'])],
]);

/** Matches a comparison operator ENDING at `end` (whitespace-tolerant on the
 *  right, newlines included — C3 sol finding 4). Symbolic `= != <> <= >= < >`
 *  plus `[NOT ]` + LIKE/ILIKE and the engine's own pattern-match operators.
 *  Compound non-comparison glyphs (`:=`, `=>`, `<=>`, sqlite `==`) fall out
 *  naturally: the char left of the matched operator fails the LHS-operand
 *  scan. `engine` is optional so callers that predate the dialect list keep
 *  the shared LIKE/ILIKE behavior. */
export function matchComparisonOperator(
  text: string,
  end: number,
  lowBound: number,
  engine?: string,
): ComparisonOperatorMatch | undefined {
  let i = end;
  while (i > lowBound && /\s/.test(text[i - 1]!)) i -= 1;
  if (i <= lowBound) return undefined;
  for (const op of TWO_CHAR_OPS) {
    if (i - 2 >= lowBound && text.slice(i - 2, i) === op) {
      return { operator: op, negated: false, opStart: i - 2 };
    }
  }
  const ch = text[i - 1]!;
  if (ch === '=' || ch === '<' || ch === '>') {
    return { operator: ch, negated: false, opStart: i - 1 };
  }
  let w = i;
  while (w > lowBound && /[A-Za-z]/.test(text[w - 1]!)) w -= 1;
  const word = text.slice(w, i).toUpperCase();
  const dialectOps = engine === undefined ? undefined : DIALECT_WORD_OPS.get(engine);
  if (!WORD_OPS.has(word) && !(dialectOps?.has(word) ?? false)) return undefined;
  let k = w;
  while (k > lowBound && /\s/.test(text[k - 1]!)) k -= 1;
  let nw = k;
  while (nw > lowBound && /[A-Za-z]/.test(text[nw - 1]!)) nw -= 1;
  if (text.slice(nw, k).toUpperCase() === 'NOT') {
    return { operator: word, negated: true, opStart: nw };
  }
  return { operator: word, negated: false, opStart: w };
}

export type LhsOperand = {
  readonly start: number;
  readonly end: number;
  readonly shape: 'ref' | 'call' | 'number';
};

/** Scans the LHS operand ENDING just before `end` (whitespace-tolerant).
 *  COMPLETE forms only (D2): identifier / dotted ref (optionally quoted per
 *  segment), a `)`-closed call (or paren group), or a numeric literal.
 *  Anything else (`:`, `*`, a lone operator char, digit-led non-numeric) →
 *  undefined and the recognizer does not fire. */
export function scanLhsOperand(
  text: string,
  end: number,
  lowBound: number,
): LhsOperand | undefined {
  let i = end;
  while (i > lowBound && /\s/.test(text[i - 1]!)) i -= 1;
  if (i <= lowBound) return undefined;
  if (text[i - 1] === ')') {
    // Balanced-paren walk back to the opening `(`; single-quoted spans inside
    // the argument list are skipped wholesale (no backward `''` disambiguation
    // — best-effort, guarded by the corpus).
    let depth = 1;
    let j = i - 2;
    while (j >= lowBound && depth > 0) {
      const c = text[j];
      if (c === "'") {
        j -= 1;
        while (j >= lowBound && text[j] !== "'") j -= 1;
        j -= 1;
        continue;
      }
      if (c === ')') depth += 1;
      else if (c === '(') {
        depth -= 1;
        if (depth === 0) break;
      }
      j -= 1;
    }
    if (depth !== 0 || j < lowBound) return undefined;
    const callee = scanDottedRefStart(text, j, lowBound);
    return { start: callee ?? j, end: i, shape: 'call' };
  }
  const start = scanDottedRefStart(text, i, lowBound);
  if (start === undefined) return undefined;
  const raw = text.slice(start, i);
  if (/^[0-9]+(\.[0-9]+)?$/.test(raw)) return { start, end: i, shape: 'number' };
  // A digit-led run that is not a plain number (`1abc`) is no complete operand.
  if (/^[0-9]/.test(raw)) return undefined;
  return { start, end: i, shape: 'ref' };
}

/** Start offset of a dotted, per-segment-optionally-quoted ref ending just
 *  before `end` (ws-tolerant around dots); undefined when none is visible. */
function scanDottedRefStart(text: string, end: number, lowBound: number): number | undefined {
  let i = end;
  while (i > lowBound && /\s/.test(text[i - 1]!)) i -= 1;
  for (;;) {
    const segEnd = i;
    const ch = i > lowBound ? text[i - 1]! : '';
    if (ch === '"' || ch === '`' || ch === ']') {
      const open = ch === ']' ? '[' : ch;
      let j = i - 2;
      while (j >= lowBound && text[j] !== open) j -= 1;
      if (j < lowBound) return undefined;
      i = j;
    } else {
      while (i > lowBound && /[A-Za-z0-9_$]/.test(text[i - 1]!)) i -= 1;
    }
    // Empty segment: nothing before the cursor-side end, or a dangling dot.
    if (i === segEnd) return undefined;
    let k = i;
    while (k > lowBound && /\s/.test(text[k - 1]!)) k -= 1;
    if (k > lowBound && text[k - 1] === '.') {
      i = k - 1;
      while (i > lowBound && /\s/.test(text[i - 1]!)) i -= 1;
      continue;
    }
    return i;
  }
}
